import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, getDocs, Timestamp, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import MemoryCard, { Memory } from '../components/MemoryCard';
import LoadingSpinner from '../components/LoadingSpinner';
import DreamModal from '../components/DreamModal';
import { motion, AnimatePresence } from 'framer-motion';

// Utility function to check if device is mobile
const isMobile = () => window.innerWidth <= 768;

// Star component for the background
const Star = ({ size, delay, duration }: { size: number; delay: number; duration: number }) => (
  <motion.div
    className="absolute rounded-full bg-dream-200"
    style={{
      width: size,
      height: size,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
    }}
    initial={{ opacity: 0 }}
    animate={{
      opacity: [0, 0.8, 0],
    }}
    transition={{
      duration: duration * (isMobile() ? 1.5 : 1),
      delay: delay,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "linear",
    }}
  />
);

// Floating orb effect
const DreamOrb = ({ delay }: { delay: number }) => (
  <motion.div
    className="absolute rounded-full bg-gradient-to-b from-dream-400/10 to-dream-500/10 blur-xl"
    style={{
      width: Math.random() * 100 + 50,
      height: Math.random() * 100 + 50,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
    }}
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{
      opacity: [0.3, 0.6, 0.3],
      scale: [0.8, 1.1, 0.8],
      y: [0, -30, 0],
    }}
    transition={{
      duration: Math.random() * 5 + 5,
      delay: delay,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut",
    }}
  />
);

const HomePage = () => {
  const [memories, setMemories] = useState<Memory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileView, setIsMobileView] = useState(isMobile());

  // Check if we should show the create button
  const shouldShowCreateButton = user && 
    !selectedMemory && // Hide when modal is open
    location.pathname !== '/create' && // Hide on create page
    !location.pathname.startsWith('/edit/'); // Hide on edit pages

  useEffect(() => {
    const fetchPublicMemories = async () => {
      try {
        const q = query(
          collection(db, 'dreams'),
          where('isPublic', '==', true),
          orderBy('createdAt', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const fetchedMemories: Memory[] = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedMemories.push({
            id: doc.id,
            title: data.title || 'Untitled Dream',
            content: data.content || 'No content available',
            tags: Array.isArray(data.tags) ? data.tags : [], // Ensure tags is always an array
            isPublic: Boolean(data.isPublic),
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
            userId: data.userId || 'anonymous',
            userDisplayName: data.userDisplayName,
            userIcon: data.userIcon,
            authorName: data.authorName
          } as Memory);
        });

        setMemories(fetchedMemories);
      } catch (err) {
        console.error('Error fetching dreams:', err);
        setError('Failed to load dreams. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPublicMemories();
  }, []);

  // Add resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMemoryClick = (memory: Memory) => {
    console.log('Memory clicked:', memory);
    setSelectedMemory(memory);
  };

  const handleEdit = async (memory: Memory) => {
    console.log('handleEdit called with memory:', memory);
    console.log('Current user:', user);
    
    if (!user) {
      console.log('No user logged in');
      return;
    }
    
    if (user.uid !== memory.userId) {
      console.log('User does not own this dream');
      return;
    }
    
    try {
      console.log('Attempting to navigate to edit page');
      setSelectedMemory(null); // Close the modal first
      
      // Use a timeout to ensure the modal is closed before navigation
      setTimeout(() => {
        console.log('Executing navigation to:', `/edit/${memory.id}`);
        navigate(`/edit/${memory.id}`, { replace: true });
      }, 0);
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  const handleDelete = async (memoryId: string) => {
    console.log('handleDelete called with memoryId:', memoryId);
    
    const memoryToDelete = memories.find(m => m.id === memoryId);
    
    if (!user || !memoryToDelete || user.uid !== memoryToDelete.userId) {
      console.log('Delete not allowed:', { 
        hasUser: !!user, 
        hasMemory: !!memoryToDelete, 
        isOwner: user?.uid === memoryToDelete?.userId 
      });
      return;
    }

    try {
      console.log('Attempting to delete dream from database');
      await deleteDoc(doc(db, 'dreams', memoryId));
      setMemories(memories.filter(memory => memory.id !== memoryId));
      setSelectedMemory(null);
      console.log('Dream deleted successfully');
    } catch (error) {
      console.error('Error deleting dream:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen text-dream-200">
        {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-dream-950 via-dream-900 to-dream-950 overflow-hidden relative">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-dream-950/80 via-transparent to-dream-950/80"></div>
        
        {/* Generate stars */}
        {Array.from({ length: isMobileView ? 15 : 30 }).map((_, i) => (
          <Star
            key={`star-${i}`}
            size={Math.random() * 2 + 1}
            delay={Math.random() * 5}
            duration={Math.random() * 3 + 2}
          />
        ))}

        {/* Generate floating orbs */}
        {Array.from({ length: 8 }).map((_, i) => (
          <DreamOrb key={`orb-${i}`} delay={Math.random() * 5} />
        ))}
      </div>

      {/* Content */}
      <div className="relative min-h-screen">
        {/* Hero Section */}
        <div className="relative h-screen flex flex-col items-center justify-center px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto relative"
          >
            {/* Title with glow effect */}
            <motion.h1 
              className="text-6xl md:text-8xl font-bold text-dream-50 mb-6 relative"
              initial={{ opacity: 0, y: 20 }}
              animate={{ 
                opacity: 1, 
                y: 0,
                transition: {
                  duration: 0.8,
                  ease: "easeOut"
                }
              }}
              whileHover={{
                y: -5,
                transition: { duration: 0.3 }
              }}
            >
              <span className="relative inline-block">
                DriftRealm
                <motion.div
                  className="absolute -inset-4 bg-dream-500/20 rounded-full blur-2xl"
                  animate={{
                    opacity: [0.2, 0.5, 0.2],
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse",
                  }}
                />
              </span>
            </motion.h1>

            {/* Animated tagline */}
            <motion.div 
              className="text-xl md:text-2xl text-dream-300 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ 
                opacity: 1, 
                y: 0,
                transition: {
                  duration: 0.8,
                  delay: 0.2,
                  ease: "easeOut"
                }
              }}
              whileHover={{
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
            >
              <span className="relative">
                Dreams from every corner of the world,<br />
                floating through the collective consciousness
                <motion.div
                  className="absolute -inset-2 bg-dream-500/5 rounded-lg blur-xl"
                  animate={{
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse",
                  }}
                />
              </span>
            </motion.div>

            {/* Join button with enhanced effects */}
            {!user && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  transition: {
                    duration: 0.8,
                    delay: 0.4,
                    ease: "easeOut"
                  }
                }}
                whileHover={{
                  scale: 1.05,
                  transition: { duration: 0.3 }
                }}
              >
                <Link
                  to="/login"
                  className="inline-block px-8 py-4 bg-dream-800/50 text-dream-100 rounded-lg 
                           hover:bg-dream-700/50 transition-all duration-300
                           border border-dream-700/30 hover:border-dream-600/50
                           text-lg font-medium relative overflow-hidden group"
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-dream-500/20 to-dream-400/20"
                    initial={{ x: "-100%" }}
                    animate={{ x: "100%" }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  />
                  <motion.div
                    className="absolute inset-0 bg-dream-500/10 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  />
                  <span className="relative">Join the Dreamscape</span>
                </Link>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Dreams Grid Section */}
        <div className="container mx-auto px-4 py-12">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl font-bold text-dream-50 mb-12 text-center relative"
          >
            <span className="relative inline-block">
              Global Dreamscape
              <motion.div
                className="absolute -inset-4 bg-dream-500/10 rounded-full blur-xl"
                animate={{
                  opacity: [0.2, 0.4, 0.2],
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
              />
            </span>
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence>
              {memories.map((memory, index) => (
                <motion.div
                  key={memory.id}
                  initial={{ opacity: 0, y: 50, scale: 0.9 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0, 
                    scale: 1,
                    transition: {
                      duration: 0.5,
                      delay: index * 0.1,
                      type: "spring",
                      stiffness: 100
                    }
                  }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  whileHover={{ 
                    y: -10,
                    scale: 1.02,
                    transition: { duration: 0.2 }
                  }}
                  className="relative group"
                >
                  <motion.div
                    className="absolute -inset-2 bg-gradient-to-r from-dream-500/10 to-dream-400/10 
                              rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"
                  />
                  <MemoryCard
                    memory={memory}
                    onClick={() => handleMemoryClick(memory)}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {(!memories || memories.length === 0) && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center text-dream-200 mt-8"
            >
              No dreams found. Be the first to share your dream!
            </motion.div>
          )}
        </div>

        {/* Create Dream Button with enhanced effects */}
        {shouldShowCreateButton && (
          <Link
            to="/create"
            className="fixed bottom-8 right-8 group"
            aria-label="Record a new dream"
          >
            <motion.div
              className="absolute -inset-4 bg-dream-500/20 rounded-full blur-2xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            />
            <div className="relative flex items-center justify-center w-16 h-16 
                         rounded-full bg-gradient-to-r from-dream-500 to-dream-400 
                         text-dream-50 shadow-lg transition-all duration-300 
                         group-hover:scale-110 group-hover:shadow-dream-500/30
                         border border-dream-400/30">
              <motion.svg 
                className="w-8 h-8" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                animate={{
                  rotate: [0, 90],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 0.3,
                  ease: "easeInOut",
                }}
                whileHover={{
                  scale: 1.1,
                }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M12 4v16m8-8H4" />
              </motion.svg>
            </div>
            <span className="absolute right-full mr-4 top-1/2 -translate-y-1/2 py-2 px-4
                         bg-dream-800/90 text-dream-100 rounded-lg text-sm whitespace-nowrap
                         opacity-0 group-hover:opacity-100 transition-opacity duration-300
                         border border-dream-700/30 pointer-events-none">
              Record a new dream
            </span>
          </Link>
        )}

        {selectedMemory && (
          <DreamModal
            memory={selectedMemory}
            onClose={() => setSelectedMemory(null)}
            onDelete={user && user.uid === selectedMemory.userId ? handleDelete : undefined}
            onEdit={user && user.uid === selectedMemory.userId ? handleEdit : undefined}
          />
        )}
      </div>
    </div>
  );
};

export default HomePage; 